import React, { useState, useEffect, useLayoutEffect, useRef } from 'react';
import { motion } from 'framer-motion';
import styles from './Assistant.module.css';
import { useAssistant } from './AssistantContext';

export const Eye = React.memo(({ style, eyeControls, blinkControls }) => {
  return (
    <motion.div className={styles.eye} style={style} animate={blinkControls}>
      <motion.div className={styles.pupil} animate={eyeControls} />
    </motion.div>
  );
});

export const Mouth = React.memo(({ expression }) => {
  const mouthExpressions = {
    default: styles.default,
    happy: styles.happy,
    sad: styles.sad,
    surprised: styles.surprised,
    thinking: styles.thinking,
  };

  const mouthClass = mouthExpressions[expression] || styles.default;
  return <div className={`${styles.mouth} ${mouthClass}`} />;
});

export function calculateBubblePosition(position, assistantSize = 75, offset = 10, bubbleDimensions) {
  const screenWidth = window.innerWidth;
  const screenHeight = window.innerHeight;

  const spaceRight = screenWidth - (position.x + assistantSize);
  const spaceLeft = position.x;
  const spaceTop = position.y;
  const spaceBottom = screenHeight - (position.y + assistantSize);

  const bubbleWidth = bubbleDimensions.width || 300;
  const bubbleHeight = bubbleDimensions.height || 30;

  let placement = 'rightTop';

  const preferRight = spaceRight >= bubbleWidth + offset;
  const preferTop = spaceTop >= bubbleHeight + offset;
  const preferBottom = spaceBottom >= bubbleHeight + offset && spaceBottom >= spaceTop;
  const preferRightBottom = spaceBottom >= 2 * spaceTop && spaceRight > 2 * spaceLeft;

  const left = preferRight
    ? Math.min(position.x + assistantSize + offset, screenWidth - bubbleWidth)
    : Math.max(position.x - bubbleWidth - offset, 0);

  const top = preferBottom
    ? Math.min(position.y + assistantSize + offset + 25, screenHeight - bubbleHeight)
    : Math.max(position.y - bubbleHeight - offset, 0);

  if (preferRightBottom) placement = 'rightBottom';
  else if (preferRight && preferTop) placement = 'rightTop';
  else if (preferRight && !preferTop) placement = 'rightBottom';
  else if (!preferRight && preferTop) placement = 'leftTop';
  else placement = 'leftBottom';

  const style = {
    left,
    top,
    maxWidth: bubbleDimensions.fixed ? 'unset' : bubbleWidth,
    ...(bubbleDimensions.fixed && {
      '--bubble-width': `${bubbleDimensions.width}px`,
      '--bubble-height': `${bubbleDimensions.height}px`,
    }),
  };

  return { style, placement };
}

export const SpeechBubble = React.memo(({ position }) => {
  const { state, actions } = useAssistant();
  const { bubbleText, bubbleSize, messageVariant } = state;
  const [isVisible, setIsVisible] = useState(false);
  const [showOkButton, setShowOkButton] = useState(false);
  const bubbleRef = useRef(null);
  // Reference no longer needed for useLayoutEffect, but kept for potential future features

  useEffect(() => {
    if (!bubbleSize.fixed) {
      setIsVisible(false);
      return;
    }

    const showTimer = setTimeout(() => {
      setIsVisible(true);
    }, 50);

    return () => {
      clearTimeout(showTimer);
      setIsVisible(false);
    };
  }, [bubbleSize.fixed]);

  useEffect(() => {
    if (!bubbleText || messageVariant !== 'sticky') {
      setShowOkButton(false);
      return;
    }

    const speed = state.config?.textTypingSpeed || 25;
    const delay = Math.min(6000, Math.max(2000, bubbleText.length * speed - 800));

    const timer = setTimeout(() => {
      setShowOkButton(true);
    }, delay);

    return () => clearTimeout(timer);
  }, [bubbleText, messageVariant, state.config?.textTypingSpeed]);

  // Size is measured and locked once during initial measurement.
  // No runtime corrections - bubble size is fixed to prevent jumping during text animation.
  // Minor overflow is acceptable for smooth UX.

  if (!bubbleText && !bubbleSize.fixed) return null;

  const { style: bubbleStyle, placement } = calculateBubblePosition(position, 75, 10, bubbleSize);
  const bubbleClass = `${styles.bubble} ${styles[placement]} ${bubbleSize.fixed ? styles['fixed-size'] : ''} ${isVisible ? styles.visible : ''}`;

  return state.bubbleText ? (
    <div
      className={`${bubbleClass} ${bubbleSize.fixed ? 'fixed-size' : ''}`}
      style={bubbleStyle}
    >
      {state.bubbleText}
      {messageVariant === 'sticky' && showOkButton && (
        <button
          className={`${styles.bubbleOkBtn} ${showOkButton ? styles.bubbleOkBtnVisible : ''}`}
          onClick={() => {
            actions.dismiss('ok_clicked');
            setShowOkButton(false);
          }}
        >
          Ok
        </button>
      )}
    </div>
  ) : null;
});
